import { alertNotification } from "../../actions/alertNotification";
import { setAppUrl, setShowSelectOrganization } from "../../actions/app";
import { setCurrUser, updatePasswordSuccess } from "../../actions/auth";
import { updateCompanyDetails } from "../../actions/company";
import { getUserDetails } from "../../actions/userProfile";
import { getFeatureStatus } from "../../components/Admin/admin.service";
import { handleNewUser } from "../../components/SignUp/signup.helper";
import { AUTH_TOKENS, notifyIcon, requestMethod, route } from "../../config";
import { label, number, quote, requestStatus } from "../../config/constants";
import config from "../../env.config";
import { verifyNewAdminUser, verifyNewUser } from "../../helper/sendMailPayload";
import { getCookies, setCookiesOnDomain } from "../../helper/setCookies";
import store from "../../store";
import { trackLogin, trackRedirectToHomePage, trackSignUp } from "../../utils/analytics";
import { checkApiSuccess } from "../../utils";
import setAuthorizationToken from "../../utils/setAuthorizationToken";
import { setTenantOrganizationIdHeader } from "../../utils/setTenantOrganizationHeader";
import { fetch, logoutUser } from "./api.service";
import { sendResetMail } from "./mail.service";
import { saveState } from "./session.service";
import { sendNewUserVerifyMail, updateProfileStatus } from "./usersetup.service";
import { getNotification } from "../../utils/common";

const buildLoginRequestData = (creds) => {
  const requestData = { ...creds };
  if (creds.isGoogleLogin && creds.idToken) {
    requestData.idToken = creds.idToken;
  }
  return requestData;
};

/**
 * handling api for local login and settting tokens
 *
 * @param {object} creds - User credentials (username and password).
 * @param {boolean} isSignupIncomplete - Indicates if user signup is incomplete.
 * @param {boolean} isNewUser - Indicates if the user is new.
 * @param {object} history - History object for navigation.
 * @returns {Promise<object>} - Promise that resolves with the login response.
 * @author Himanshi
 */
export const loginApi = (creds, history, isSignupIncomplete, isNewUser) => {
  return async (dispatch) => {
    const requestData = buildLoginRequestData(creds);

    let requestPayload = {
      method: requestMethod.POST,
      url: `${config.BASE_URL.BASE_API_URL}/userLogin`,
      data: requestData,
    };
    let response = await fetch(requestPayload, false, true);
    if (checkApiSuccess(response)) {
      const loginMethod = requestData.isGoogleLogin ? "google" : requestData.otp ? "otp" : "email";
      await setStateOnLogin({ dispatch, response, history, isNewUser, loginMethod });
      if (!isSignupIncomplete) dispatch(getUserDetails(response.data.user.id));
      let redirectUrl = sessionStorage.getItem("redirectUrl");
      if (redirectUrl) {
        history.push(redirectUrl);
      } else {
        history.push(route.PRIVATE_ROUTE.LANDING_PAGE.PATH);
        trackRedirectToHomePage();
      }
      if (window.location.origin !== config.BASE_URL.APP_URL) {
        await setCookiesOnDomain(AUTH_TOKENS.IS_TENANT_DOMAIN_ACTIVE, true, config.APP_BASE_DOMAIN);
      }
      return response;
    } else {
      if (response && response.data) {
        dispatch(alertNotification(true, response.data.message, notifyIcon.ERROR_ICON));
        dispatch(setShowSelectOrganization(false));
        history.push(route.ROUTE.LOGIN.PATH);
      }
      return response;
    }
  };
};

/**
 * Handle login, redirection for tenant, call loginApi
 * @param {Object} creds
 * @param {Object} history
 * @param {String} companySlug
 * @param {Boolean} isSignupIncomplete
 * @param {Boolean} isNewUser
 * @returns
 * @author Himanshi
 */
export const login = (creds, history, companySlug, isSignupIncomplete, isNewUser) => {
  return async (dispatch) => {
    let isTenantDomainActive = await getCookies(AUTH_TOKENS.IS_TENANT_DOMAIN_ACTIVE);
    const hostname = window.location.hostname;
    const hostnameMatch = hostname.match(config.APP_WILDCARD_REGEX);
    const isOnWildcardDomain = hostnameMatch && hostnameMatch[number.ONE] === config.APP_WILDCARD;
    
    if (
      companySlug &&
      isOnWildcardDomain &&
      !isTenantDomainActive
    ) {
      let newAppUrl = config.BASE_URL.APP_URL.replace(config.APP_WILDCARD, companySlug);
      const redirectUrl = sessionStorage.getItem("redirectUrl");
      sessionStorage.removeItem("redirectUrl");
      config.BASE_URL.APP_URL = newAppUrl;
      dispatch(setAppUrl(newAppUrl));

      // Set cookies BEFORE redirect
      await setCookiesOnDomain(AUTH_TOKENS.TENANT_DOMAIN, newAppUrl, process.env.REACT_APP_URL);
      await setCookiesOnDomain(AUTH_TOKENS.IS_TENANT_DOMAIN_ACTIVE, true, config.APP_BASE_DOMAIN);

      const initiatePayload = {
        method: requestMethod.POST,
        url: `${config.BASE_URL.BASE_API_URL}/initiateTenantLogin`,
        data: buildLoginRequestData(creds),
      };
      const initiateRes = await fetch(initiatePayload, false, true);

      if (!checkApiSuccess(initiateRes) || !initiateRes?.data?.data?.redirectUrl) {
        dispatch(
          alertNotification(
            true,
            initiateRes?.data?.message || "Failed to initiate tenant login. Please try again.",
            notifyIcon.ERROR_ICON
          )
        );
        return initiateRes;
      }

      let finalUrl = initiateRes.data.data.redirectUrl;
      const postLoginRedirect = redirectUrl ?? route.PRIVATE_ROUTE.LANDING_PAGE.PATH;
      finalUrl += `${finalUrl.includes("?") ? "&" : "?"}redirectUrl=${encodeURIComponent(postLoginRedirect)}`;

      window.location.replace(finalUrl);
      return { redirected: true };
    } else {
      return dispatch(loginApi(creds, history, isSignupIncomplete, isNewUser));
    }
  };
};

/**
 * common function for state set on login
 * @param {Object}
 * @author Himanshi
 */
const setStateOnLogin = async ({ dispatch, response, history, isNewUser, loginMethod = "email" }) => {
  await setCookiesOnDomain(AUTH_TOKENS.JWT_TOKEN, response.data.accessTokenDB, window.location.hostname);
  await setCookiesOnDomain(AUTH_TOKENS.REFRESH_TOKEN, response.data.refreshTokenDB, window.location.hostname);
  await saveState(AUTH_TOKENS.USER, response.data.user);
  await setAuthorizationToken(response.data.accessTokenDB);
  dispatch(setCurrUser(response.data.user));
  trackLogin(loginMethod);
  if (isNewUser) {
    await handleNewUser(dispatch, response, history);
  }
  setTenantOrganizationIdHeader(response.data.user?.companyId);
  await getFeatureStatus(response.data.user?.companyId);
};

/**
 * handling api for local signup
 * @param {*} request
 * @returns {void}
 */

export const signup = (user, history, isNewCompany=false, token) => {
  const existingCompanyUrl = `${config.BASE_URL.BASE_API_URL}/userSignup`;
  const newCompanyUrl = `${config.BASE_URL.BASE_API_URL}/companySignup`;
  return async (dispatch) => {
    const requestPayload = {
        method: requestMethod.POST,
        url: isNewCompany ? newCompanyUrl : existingCompanyUrl,
        data: { ...user, ...(token ? { signupToken : token } : {}) },
      },
      response = await fetch(requestPayload, true, false);
    if (response && response.data.success) {
      trackSignUp(user?.isGoogleSignUp ? "google" : "email");
      handleSignupRes(dispatch, response.data, user, history);
      return response;
    }
  };
};

/**
 * handling api for reset password and forgotpassword link mail
 * @param {userEmail}
 * @returns {void}
 */

export const resetPassword = (payload, history, isAdmin) => {
  return async (dispatch) => {
    const requestPayload = {
        method: requestMethod.POST,
        url: `${config.BASE_URL.BASE_API_URL}/resetPassword`,
        data: payload,
      },
      resetRes = await fetch(requestPayload, true, false);
    if (checkApiSuccess(resetRes)) {
      if (resetRes && resetRes.status === requestStatus.OK) {
        getNotification(resetRes?.data?.message, notifyIcon.SUCCESS_ICON);
        dispatch(setShowSelectOrganization(false));
      } else {
        getNotification(resetRes?.data?.message, notifyIcon.ERROR_ICON);
      }
    }
    return resetRes;
  };
};

/**
 * handling api for update password
 * @param {credential of the user}
 * @returns {void}
 */
export const updatePassword = (creds) => {
  return async (dispatch) => {
    const requestPayload = {
        method: requestMethod.POST,
        url: `${config.BASE_URL.BASE_API_URL}/updatePassword`,
        data: {
          newPassword: creds.password,
          token: creds.token,
        },
      },
      response = await fetch(requestPayload, true, true);
    if (checkApiSuccess(response)) {
      dispatch(updatePasswordSuccess(true));
      dispatch(alertNotification(true, response.data.message, notifyIcon.SUCCESS_ICON));
    }
  };
};

export const verifyUser = (payload) => {
  return async (dispatch) => {
    const requestPayload = {
        method: requestMethod.GET,
        url: `${config.BASE_URL.BASE_API_URL}/app/verifyUser`,
        params: payload,
      },
      response = await fetch(requestPayload, true, false);
    if ((response && !response.data.success) || !response) {
      dispatch(logoutUser());
    } else if (checkApiSuccess(response)) {
      return response.data.data;
    }
  };
};

/**
 * handling api for change password
 * @param {user credential}
 * @returns {void}
 */

export const changePassword = (creds) => {
  return async (dispatch) => {
    const requestPayload = {
        method: requestMethod.POST,
        url: `${config.BASE_URL.BASE_API_URL}/app/changePassword`,
        data: {
          newPassword: creds.newPassword,
          emailId: creds.emailId,
          currentPassword: creds.currentPassword,
        },
      },
      response = await fetch(requestPayload, true, true);
    if (checkApiSuccess(response)) {
      dispatch(updatePasswordSuccess(true));
      dispatch(alertNotification(true, response.data.message, notifyIcon.SUCCESS_ICON));
    } else {
      if (response && response.data) {
        dispatch(alertNotification(true, response.data.message, notifyIcon.ERROR_ICON));
      }
    }
  };
};

/**
 * Handles action after sign up success
 * @param {Function} dispatch - Redux dispatch function.
 * @param {object} signupRes - The signup response object.
 * @param {object} user - The user object.
 * @param {object} history - History object for navigation.
 * @param {object} adminUserOnboardData - Data for admin user onboarding.
 * @param {object} profileSetUp - Profile setup data.
 * @author Bhavana
 */
const handleSignupRes = async (dispatch, signupRes, user, history) => {
  if (signupRes?.data) {
    const response = signupRes.data;
    let data;
    if (signupRes.success && !user?.profileSetup && response?.accountStatus === "AccountNotFound") {
      response?.signUpComplete && dispatch(alertNotification(true, quote.EMAIL_VERIFICATION, notifyIcon.SUCCESS_ICON));
    } else if (signupRes.success && user?.profileSetup) {
      dispatch(alertNotification(true, quote.PROFILE_SETUP, notifyIcon.SUCCESS_ICON));
      await updateProfileStatus(user.actor);
      const loginCreds = {
        username: user.actor,
        password: user.password,
        companyId: response.companyId,
        isGoogleLogin: user.isGoogleSignUp,
        idToken: user.idToken,
      };
      if (user.isGoogleSignUp && user.idToken) {
        loginCreds.idToken = user.idToken;
      }
      dispatch(login(loginCreds, history, response.companySlug, true, true));
    } else if (signupRes.success && !response.signUpComplete) {
      dispatch(alertNotification(true, signupRes.message, notifyIcon.WARNING_ICON));
      dispatch(updateCompanyDetails(signupRes));
    }
  } else {
    signupRes.message
      ? dispatch(alertNotification(true, signupRes.message, notifyIcon.WARNING_ICON))
      : dispatch(alertNotification(true, label.ERR_MESSAGE, notifyIcon.ERROR_ICON));
  }
};

export const getUserCompanies = (payload) => {
  return async (dispatch) => {
    const requestPayload = {
        method: requestMethod.GET,
        url: `${config.BASE_URL.BASE_API_URL}/user/getUserCompanies`,
        params: payload,
      },
      response = await fetch(requestPayload, true, false);
    if (checkApiSuccess(response)) {
      return response.data.data;
    }
    return [];
  };
};

/**
 * Generate OTP for phone number login
 * @author Muskan Thakur
 */
export const generateOtp = (payload) => {
  return async (dispatch) => {
    const requestPayload = {
        method: requestMethod.POST,
        url: `${config.BASE_URL.BASE_API_URL}/generateOtp`,
        data: payload,
      },
      response = await fetch(requestPayload, true, false);
    if (checkApiSuccess(response)) {
      dispatch(alertNotification(true, response.data.message, notifyIcon.SUCCESS_ICON));
      return response.data;
    } else {
      dispatch(alertNotification(true, response.data.message, notifyIcon.ERROR_ICON));
    }
  };
};

/**
 * Send OTP for in-app password reset via the generateOtpByType endpoint.
 * @param {Object} payload - { methods: ['email'|'mobile'], email, mobile }
 */
export const sendOtpForPasswordReset = (payload) => {
  return async (dispatch) => {
    const { methods = [] } = payload;
    const hasEmail = methods.includes("email");
    const hasMobile = methods.includes("mobile");
    const otpType = hasEmail && hasMobile ? "both" : hasEmail ? "email" : "mobile";

    const requestPayload = {
      method: requestMethod.POST,
      url: `${config.BASE_URL.BASE_API_URL}/generateOtpResetPassword`,
      data: { otpType },
    };
    const response = await fetch(requestPayload, true, true);
    if (checkApiSuccess(response)) {
      dispatch(alertNotification(true, response.data.message, notifyIcon.SUCCESS_ICON));
      return { success: true };
    } else {
      if (response && response.data) {
        dispatch(alertNotification(true, response.data.message, notifyIcon.ERROR_ICON));
      }
      return { success: false };
    }
  };
};

/**
 * Verify OTP for in-app password reset flow.
 * On success returns the otpVerifiedToken needed for the next step.
 * @param {Object} payload - { otp: string }
 * @returns {{ success: boolean, otpVerifiedToken?: string }}
 */
export const verifyOtpForPasswordReset = (payload) => {
  return async (dispatch) => {
    const requestPayload = {
      method: requestMethod.POST,
      url: `${config.BASE_URL.BASE_API_URL}/verifyOtpResetPassword`,
      data: { otp: Number(payload.otp) },
    };
    const response = await fetch(requestPayload, true, true);
    if (checkApiSuccess(response)) {
      dispatch(alertNotification(true, response.data.message, notifyIcon.SUCCESS_ICON));
      return { success: true, otpVerifiedToken: response.data.data?.otpVerifiedToken };
    } else {
      if (response && response.data) {
        dispatch(alertNotification(true, response.data.message, notifyIcon.ERROR_ICON));
      }
      return { success: false };
    }
  };
};

/**
 * Verify OTP via the email link token (skips manual OTP entry).
 * @param {Object} payload - { token: string }
 * @returns {{ success: boolean, otpVerifiedToken?: string }}
 */
export const verifyOtpByEmailLink = (payload) => {
  return async (dispatch) => {
    const requestPayload = {
      method: requestMethod.POST,
      url: `${config.BASE_URL.BASE_API_URL}/verifyOtpByEmailLinkResetPassword`,
      data: { token: payload.token },
    };
    const response = await fetch(requestPayload, true, true);
    if (checkApiSuccess(response)) {
      return { success: true, otpVerifiedToken: response.data.data?.otpVerifiedToken };
    } else {
      if (response && response.data) {
        dispatch(alertNotification(true, response.data.message, notifyIcon.ERROR_ICON));
      }
      return { success: false };
    }
  };
};

/**
 * Set a new password after OTP verification.
 * @param {Object} payload - { token: string, password: string (hashed) }
 */
export const resetPasswordAfterOtp = (payload) => {
  return async (dispatch) => {
    const requestPayload = {
      method: requestMethod.POST,
      url: `${config.BASE_URL.BASE_API_URL}/resetPasswordVerify`,
      data: { token: payload.token, password: payload.password },
    };
    const response = await fetch(requestPayload, true, true);
    if (checkApiSuccess(response)) {
      dispatch(alertNotification(true, response.data.message, notifyIcon.SUCCESS_ICON));
      return { success: true };
    } else {
      if (response && response.data) {
        dispatch(alertNotification(true, response.data.message, notifyIcon.ERROR_ICON));
      }
      return { success: false };
    }
  };
};

export const checkIfCompanyAlreadyExists = (company) => {
  return async (dispatch) => {
    const requestPayload = {
        method: requestMethod.GET,
        url: `${config.BASE_URL.BASE_API_URL}/company/checkIfCompanyAlreadyExists`,
        params: {
          companyName: company,
        },
      },
      response = await fetch(requestPayload, true, true);
    if (checkApiSuccess(response)) {
      return response.data.data;
    } else {
      if (response && response.data) dispatch(alertNotification(true, response.data.message, notifyIcon.ERROR_ICON));
    }
  };
};

export const getUserProfileControlsData = async (payload) => {
  const requestPayload = {
      method: requestMethod.GET,
      url: `${config.BASE_URL.BASE_API_URL}/user/getUserProfileControlsData`,
      params: {
        companyId: payload?.companyId,
        projectId: payload?.projectId,
        drutasUserId: payload?.drutasUserId,
      },
    },
    response = await fetch(requestPayload, true, true);
  if (checkApiSuccess(response)) {
    return response.data.data;
  }
};

export const getUserAIEnabledVariables = async (payload) => {
  const requestPayload = {
      method: requestMethod.GET,
      url: `${config.BASE_URL.BASE_API_URL}/user/getUserAIEnabledVariables`,
      params: {
        companyId: payload?.companyId,
        drutasUserId: payload?.drutasUserId,
      },
    },
    response = await fetch(requestPayload, false, true);
  if (checkApiSuccess(response)) {
    return response.data.data;
  }
};

/**
 * Get company login options based on slug or companyId
 * @param {string} identifier - Slug or companyId
 * @param {Object} options - { byCompanyId: true } to send companyId instead of slug
 * @author Muskan Thakur
 */
export const getCompanyLoginOptions = async (identifier, options = {}) => {
  const { byCompanyId = false } = options;
  const data = byCompanyId ? { companyId: identifier } : { slug: identifier };
  const requestPayload = {
    method: requestMethod.POST,
    url: `${config.BASE_URL.BASE_API_URL}/company/getCompanyLoginOptions`,
    data,
  };

  const response = await fetch(requestPayload, false, false);
  if (checkApiSuccess(response)) {
    return response.data.data;
  }
  return null;
};
